import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useAsyncDebounce } from 'react-table';
import { useListRepositorySigningsByUserQuery } from 'rtk/org-admin/journalRepo.slice';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 400;

export const useRepositoryNotaryJournals = ({
  userGuid,
  skip = false,
  defaultPageSize = DEFAULT_PAGE_SIZE
} = {}) => {
  const [searchString, setSearchString] = useState(null);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [pageIndex, setPageIndex] = useState(0);
  const [filterMonth, setFilterMonth] = useState({});
  const [customDateRange, setCustomDateRange] = useState(false);

  // The box updates on every keystroke; the request only follows once typing pauses, since each
  // search reads every connected notary's journal.
  const [querySearch, setQuerySearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(
      () => setQuerySearch(searchString || ''),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchString]);

  const trimmedSearch = _.trim(querySearch);
  const hasSearch = trimmedSearch.length > 0;
  const indexstart = pageIndex * pageSize;

  const {
    data: signingsRes,
    isFetching,
    isLoading: isQueryLoading,
    isError,
    error: queryError,
    refetch
  } = useListRepositorySigningsByUserQuery(
    {
      userGuid,
      indexstart,
      maxresults: pageSize,
      order: 'CreatedDescending',
      search: hasSearch ? trimmedSearch.toLowerCase() : undefined,
      createdafter: filterMonth?.begin ?? null,
      createdbefore: filterMonth?.end ?? null
    },
    { skip, refetchOnMountOrArgChange: true }
  );

  const displayEntries = isError ? [] : signingsRes?.Objects || [];
  const totalRecords = signingsRes?.TotalRecords ?? 0;
  const endOfResults = !!signingsRes?.EndOfResults;
  const loading = isFetching || isQueryLoading;
  const initialLoading = loading && displayEntries.length === 0;
  const isRefreshing = loading && displayEntries.length > 0;
  const accessUnavailable =
    isError && (queryError?.Code === 'NotAdmin' || queryError?.status === 403);
  const error = isError
    ? (typeof queryError?.Data === 'string' && queryError.Data) ||
      queryError?.Message ||
      queryError?.Text ||
      'Failed to load journal entries'
    : null;

  useEffect(() => {
    setPageIndex(0);
  }, [pageSize]);

  useEffect(() => {
    if (!customDateRange) {
      setPageIndex(0);
    }
  }, [filterMonth, customDateRange]);

  useEffect(() => {
    setPageIndex(0);
    setSearchString(null);
    setFilterMonth({});
    setCustomDateRange(false);
  }, [userGuid]);

  const onSearchChange = useAsyncDebounce(() => {
    setFilterMonth({});
    setPageIndex(0);
  }, 500);

  const onCustomDateRange = isFilter => {
    if (isFilter) {
      setPageIndex(0);
    } else {
      setCustomDateRange(false);
      setFilterMonth({});
    }
  };

  const hasSearchParams = () => hasSearch;

  return {
    searchString,
    setSearchString,
    pageSize,
    setPageSize,
    pageIndex,
    setPageIndex,
    filterMonth,
    setFilterMonth,
    customDateRange,
    setCustomDateRange,
    displayEntries,
    totalRecords,
    endOfResults,
    loading,
    initialLoading,
    isRefreshing,
    accessUnavailable,
    error,
    queryError,
    refetch,
    onSearchChange,
    onCustomDateRange,
    hasSearchParams
  };
};
